import { Col, Row } from "reactstrap";
import { useCallback, useMemo } from "react";
import {
  IEducationInfoDetails,
  ModalType,
} from "../../../../../Types/UserProfileDetails";
import { educationInformationModal } from "../../../../../utils/Constant";
import { useAppDispatch } from "../../../../../ReduxToolkit/Hooks";
import { openModal } from "../../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import EmployeeInfoCard from "../../EmployeeInfoCard";
import { setAttachment } from "../../../../../ReduxToolkit/Slice/AttachmentSlice";
import { findAndCategorizedAttachment } from "../../../../../utils/fileUtils";
import { useEmployeeProfile } from "../../../../../hooks/useEmployeeProfile";
import { canShowEditInfo, hasAccessPrivateFields } from "../../../utils/access";

const FIELD_ORDER: Array<keyof IEducationInfoDetails | string> = [
  "board",
  "institute_name",
  "schoolname",
  "course_type",
  "strtyear",
  "endyear",
  "percentage",
  "specialization",
];

const LABEL_MAP: Partial<Record<keyof IEducationInfoDetails | string, string>> =
  {
    institute: "Institute Name",
    degree: "Degree",
    strtyear: "Start Year",
    endyear: "End Year",
    percentage: "Grade/Percentage",
    specialization: "Specialization",
    schoolname: "School Name",
    course_type: "Course",
  };

const BASE_OMIT_KEYS: Array<keyof IEducationInfoDetails | string> = [
  "id",
  "user_id",
  "addedby",
  "updatedby",
  "created_at",
  "updated_at",
  "sip",
  "education_id",
  "edu_option",
  "course_id",
  "addedBy",
  "start_year",
  "end_year",
  "school_name",
];

const PREVIEW_MODAL_BY_KEY: Record<string, ModalType> = {
  certificate: "certificateModal",
};

const EmployeeEducationInfo = () => {
  const dispatch = useAppDispatch();
  const { employeeProfileDetails, loggedInEmployee } = useEmployeeProfile();

  const educationList =
    employeeProfileDetails?.employeeEducationInformation ?? [];

  // Edit permissions
  const canEdit = useMemo(
    () =>
      !!employeeProfileDetails &&
      !!loggedInEmployee &&
      canShowEditInfo(employeeProfileDetails, loggedInEmployee),
    [employeeProfileDetails, loggedInEmployee]
  );

  // Private field access (for preview)
  const employeeId = employeeProfileDetails?.jobInformation?.userId;
  const viewerId = loggedInEmployee?.id;
  const viewerRole = loggedInEmployee?.user_type;

  const hasAccess = useMemo(() => {
    if (employeeId == null || viewerId == null || !viewerRole) return false;
    return hasAccessPrivateFields(
      Number(employeeId),
      Number(viewerId),
      viewerRole
    );
  }, [employeeId, viewerId, viewerRole]);

  const handleOpenModal = useCallback(
    (modalType: ModalType, selectedId?: number) => {
      dispatch(openModal({ modalType, selectedId }));
    },
    [dispatch]
  );

  const previewableKeys = useMemo(
    () => (hasAccess ? (["certificate"] as const) : []),
    [hasAccess]
  );

  const omitKeys = useMemo(() => [...BASE_OMIT_KEYS], []);

  const handlePreview = useCallback(
    (key: string, value: unknown) => {
      if (typeof value !== "string") return;

      const file = findAndCategorizedAttachment(value);
      if (!file) return;

      const modalType = PREVIEW_MODAL_BY_KEY[key] ?? "certificateModal";
      dispatch(setAttachment(file));
      dispatch(openModal({ modalType }));
    },
    [dispatch]
  );

  if (!educationList.length) return <Row />;

  return (
    <Row>
      {educationList.map((edu) => (
        <Col
          md="6"
          key={
            edu.id ??
            `${edu.institute_name ?? edu.schoolname}-${edu.strtyear}-${
              edu.endyear
            }`
          }
          className="mb-3"
        >
          <EmployeeInfoCard<IEducationInfoDetails>
            title="Education Information"
            entity={edu}
            showEditInfo={canEdit}
            canEdit={canEdit}
            bodyClassName="p-3"
            fieldOrder={FIELD_ORDER}
            labelMap={LABEL_MAP}
            omitKeys={omitKeys}
            iconOnClick={() =>
              handleOpenModal(educationInformationModal, edu.id ?? undefined)
            }
            previewableKeys={[...previewableKeys]}
            onPreview={handlePreview}
          />
        </Col>
      ))}
    </Row>
  );
};

export default EmployeeEducationInfo;
